"use client"

import { useTranslations } from "next-intl"
import { BoothMark } from "@/components/ui/booth-mark"
import { PulseDot } from "@/components/ui/pulse-dot"

// The hiring card, on the landing page: a job posting as the Job Board shows
// it, in the same 236x272 frame as the Guest Pass card under it, so the
// two stacked cards each have their product on the right (the founder,
// 2026-10-29: the Hiring card read as empty without one).
//
// Like the Guest Pass card it is the product's own face, not a screenshot:
// the shop row, the role, its arrangement tags, when it went up, and the floor
// (Inquire left, the arrow right) a barber reads on a posting. It asks for
// nothing; it's an example, so the shop is "Your shop".
export function HiringPostCard() {
  const t = useTranslations("marketing.landing.hiring.card")
  const tags = t.raw("tags") as string[]

  // White, after the founder saw it black (2026-10-29): it reads cleaner
  // against the dark glass and pairs with the white chair tile on the Guest
  // Pass card under it. The four groups are spread evenly down the frame
  // (justify-between), so there's no dead gap above the floor.
  return (
    <div
      className="relative mx-auto flex flex-col justify-between overflow-hidden rounded-[28px] bg-white p-5 text-left text-neutral-950 shadow-[0_2px_18px_rgba(0,0,0,0.35)]"
      style={{ height: 272, width: 236 }}
    >
      <div className="flex flex-col gap-3">
        <span className="inline-flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider text-neutral-500">
          <PulseDot />
          {t("label")}
        </span>

        <div className="flex min-w-0 items-center gap-2.5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-neutral-950 text-white">
            <BoothMark size={20} />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-[12.5px] font-medium">{t("shop")}</span>
            <span className="block truncate text-[11px] text-neutral-500">{t("city")}</span>
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <p className="line-clamp-2 text-[17px] font-medium leading-snug tracking-tight">{t("title")}</p>
        {/* The Job Board's own tag colours: the kind of position in amber,
            how it pays in sky (components/hiring/hiring-browse-shell.tsx). */}
        <div className="flex flex-wrap gap-1.5">
          <span className="rounded-full border border-amber-100 bg-amber-50 px-2.5 py-0.5 text-[10.5px] font-normal text-amber-700">
            {t("role")}
          </span>
          {tags.map((tag) => (
            <span key={tag} className="rounded-full border border-sky-100 bg-sky-50 px-2.5 py-0.5 text-[10.5px] font-normal text-sky-700">
              {tag}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-neutral-500">
          {t("posted")} · {t("views")}
        </p>
      </div>

      <div className="flex min-w-0 items-center justify-between gap-2 border-t border-neutral-100 pt-3">
        <span className="text-[14px] font-medium tracking-tight text-neutral-950">{t("inquire")}</span>
        <span aria-hidden className="grid h-7 w-7 place-items-center rounded-full bg-neutral-950 text-[13px] text-white">
          →
        </span>
      </div>
    </div>
  )
}
